<script lang="ts" setup>
import type { BrokerUpdatesOrderDto, TrucksByTravelOrderId } from "@/api/trips/dto/trip.dto";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import type { Trip } from "@/types/trip";
import OmniTabs from "@/components/omni/OmniTabs.vue";

const ALL_UPDATES_TAB = "all";

const location = useRoute();

const props = defineProps<{
  trip: Trip;
}>();

const emits = defineEmits<{
  (e: "change"): void;
}>();

const uniqueOrders = ref<BrokerUpdatesOrderDto[]>([]);
const trucksByTravelOrder = ref<TrucksByTravelOrderId>({});

const activeTab = computed(() => {
  if (location.name === "trip-broker-updates-order" && location.params.orderId) {
    return `order-${location.params.orderId}`;
  }

  return ALL_UPDATES_TAB;
});

async function createTrucksByTravelOrder() {
  const entries = await Promise.all(
    props.trip.routes.flatMap(async (route) => {
      return route.travelOrders
        .filter(({ id }) => id)
        .map(({ id }) => {
          return [id, route.truck];
        });
    }),
  );

  trucksByTravelOrder.value = Object.fromEntries(entries.flat());
}

function createUniqueOrders() {
  if (!props.trip?.routes) {
    uniqueOrders.value = [];
    return;
  }

  const ordersMap: Record<string, BrokerUpdatesOrderDto> = {};

  props.trip.routes.forEach((route) => {
    route.travelOrders
      .filter(({ id }) => id)
      .forEach((travelOrder) => {
        const orderId = travelOrder.order.id || "";
        const truck = trucksByTravelOrder.value[travelOrder.id!];

        if (!ordersMap[orderId]) {
          ordersMap[orderId] = {
            ...travelOrder.order,
            travelOrders: [],
          };
        }

        ordersMap[orderId].travelOrders.push({
          ...travelOrder,
          truck,
          routeId: route.id,
        });
      });
  });

  uniqueOrders.value = Object.values(ordersMap);
}

watch(
  () => props.trip,
  async () => {
    await createTrucksByTravelOrder();
    createUniqueOrders();
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <div class="my-4">
    <omni-tabs :model-value="activeTab" class="rounded">
      <v-tab
        :to="{ name: 'trip-broker-updates-all', params: { id: trip.id } }"
        :value="ALL_UPDATES_TAB"
        class="text-uppercase"
      >
        All updates
      </v-tab>

      <v-tab
        v-for="order in uniqueOrders"
        :key="order.id"
        :to="{ name: 'trip-broker-updates-order', params: { id: trip.id, orderId: order.id } }"
        :value="`order-${order.id}`"
        class="text-uppercase"
      >
        Order #{{ order.number }}
      </v-tab>
    </omni-tabs>

    <router-view v-slot="{ Component }">
      <component :is="Component" :orders="uniqueOrders" :routes="trip.routes" @update="emits('change')" />
    </router-view>
  </div>
</template>

<style lang="scss" scoped></style>
